import { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Spin, Empty, Select } from 'antd';
import {
  FileTextOutlined,
  StarOutlined,
  CalendarOutlined,
  TeamOutlined,
} from '@ant-design/icons';

interface Event {
  id: string;
  name: string;
  slug: string;
  startDate: string;
  endDate: string;
  _count: {
    feedbacks: number;
    simpleFeedbacks: number;
  };
}

interface Stats {
  event: {
    name: string;
    startDate: string;
    endDate: string;
  };
  totalResponses: number;
  comprehensiveResponses: number;
  simpleResponses: number;
  averageRating: number;
}

export default function FeedbackDashboard() {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await fetch('/api/v1/feedback/events', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      setEvents(data);
      
      if (data.length > 0) {
        setSelectedEvent(data[0].id);
        fetchStats(data[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setLoading(false);
    }
  };

  const fetchStats = async (eventId: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/feedback/events/${eventId}/stats`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventChange = (eventId: string) => {
    setSelectedEvent(eventId);
    fetchStats(eventId);
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" tip="Loading feedback data..." />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Empty
          description="No events found. Create your first event to start collecting feedback."
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <h1>Feedback Dashboard</h1>
      
      {/* Event Selector */}
      <Card style={{ marginBottom: 24 }}>
        <label style={{ marginRight: 8, fontWeight: 'bold' }}>Select Event:</label>
        <Select
          style={{ width: 400 }}
          placeholder="Select an event"
          value={selectedEvent}
          onChange={handleEventChange}
          options={events.map((event) => ({
            label: `${event.name} (${event._count.feedbacks + event._count.simpleFeedbacks} responses)`,
            value: event.id,
          }))}
        />
      </Card>

      {/* Stats Cards */}
      {stats && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Responses"
                  value={stats.totalResponses}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Average Rating"
                  value={stats.averageRating}
                  precision={1}
                  suffix="/ 5"
                  prefix={<StarOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Comprehensive Feedback"
                  value={stats.comprehensiveResponses}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Simple Feedback"
                  value={stats.simpleResponses}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Event Details">
            <p><strong>Event Name:</strong> {stats.event.name}</p>
            <p><strong>Start Date:</strong> {new Date(stats.event.startDate).toLocaleDateString()}</p>
            <p><strong>End Date:</strong> {new Date(stats.event.endDate).toLocaleDateString()}</p>
          </Card>
        </>
      )}
    </div>
  );
}
